import { useState } from "react";
import {
  GetYoutubeVideosAsAdminQuery,
  useGetYoutubeVideosAsAdminQuery,
} from "../../../lib/apollo/graphql/generated";
import { FETCH_SIZE } from "../../../utilities/constants";
import BoxWithShadow from "../../common/components/BoxWithShadow";
import { EyeIcon, ClockIcon } from "@heroicons/react/24/outline";
import Loader from "../../common/components/Loader";
import FetchMore from "../../common/components/FetchMore";
import { useInView } from "react-intersection-observer";
import { twMerge } from "tailwind-merge";
import { Video } from "../container/VideosContainer";

interface VideoItemProps {
  video: GetYoutubeVideosAsAdminQuery["getYoutubeVideosAsAdmin"]["edges"][number];
  onSelect: (video: Video) => void;
}

function VideoItem({ video, onSelect }: VideoItemProps) {
  return (
    <li
      key={video.title}
      className={twMerge(
        "relative flex flex-col gap-3",
        !video.isInserted &&
          "group cursor-pointer transition-colors duration-300 hover:text-blue-700"
      )}
      onClick={() => {
        if (!video.isInserted) {
          onSelect({
            title: video.title,
            description: video.description || "",
            youtubeUrl: video.youtubeUrl,
            playTime: video.playTime,
            statusId: "",
            videoContentCategoryId: "",
            media: {
              file: null,
            },
          });
        }
      }}
    >
      {video.isInserted && (
        <div className="pointer-events-none absolute inset-0 z-[1] flex h-full items-center justify-center rounded-md bg-black/85">
          <p className="font-bold text-white">이미 추가된 영상</p>
        </div>
      )}
      <div className="relative z-[0] overflow-hidden rounded-md border border-zinc-300">
        <img
          src={video?.thumbnail}
          draggable={false}
          alt={`${video.title} thumbnail`}
          className="aspect-video object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-lg font-semibold leading-tight">{video.title}</p>
        <div className="flex justify-between text-sm">
          <div className="flex items-center gap-[2px]">
            <ClockIcon className="w-3" />
            <span>{video.playTime}</span>
          </div>
          <div className="flex items-center gap-[2px]">
            <EyeIcon className="w-3" />
            <span>{video.viewCount.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </li>
  );
}

interface YouTubeVideoListProps {
  onVideoSelect: (video: Video) => void;
}

export default function YouTubeVideoList({
  onVideoSelect,
}: YouTubeVideoListProps) {
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const { data, loading, fetchMore } = useGetYoutubeVideosAsAdminQuery({
    variables: {
      limit: FETCH_SIZE,
      endCursor: null,
    },
  });

  const videos = data?.getYoutubeVideosAsAdmin?.edges || [];
  const pageInfo = data?.getYoutubeVideosAsAdmin?.pageInfo;

  const onViewChange = (inView: boolean) => {
    if (!inView || isFetchingMore || !pageInfo?.hasNextPage) {
      return;
    }

    setIsFetchingMore(true);

    fetchMore({
      variables: {
        limit: FETCH_SIZE,
        endCursor: pageInfo?.endCursor,
      },
    }).finally(() => {
      setIsFetchingMore(false);
    });
  };

  const [fetchMoreRef] = useInView({
    threshold: 0.3,
    onChange: onViewChange,
  });

  if (loading) {
    return (
      <BoxWithShadow className="w-1/4 flex-shrink-0 gap-3">
        <p className="text-lg font-bold">YouTube</p>
        <Loader />
      </BoxWithShadow>
    );
  }

  return (
    <BoxWithShadow className="w-1/4 flex-shrink-0 gap-3">
      <p className="text-lg font-bold">YouTube</p>
      <ul className="flex flex-col gap-8 overflow-y-auto">
        {videos?.map((video) => (
          <VideoItem key={video.title} video={video} onSelect={onVideoSelect} />
        ))}
        {pageInfo?.hasNextPage && (
          <FetchMore
            fetchMoreRef={fetchMoreRef}
            isFetchingMore={isFetchingMore}
          />
        )}
      </ul>
    </BoxWithShadow>
  );
}
